import React, { useState } from 'react';
import { FIELD_LABELS, NOMINEE_FIELD_LABELS } from './kycLabels';
import { DocImg, DocViewer, mouTypeLabel, valueTypeLabel } from '../public/investorProfile/shared';
import { parseServerTime } from '../../../utils/serverTime';
import { formatDateToDdmmyyyy, withCountryCode } from '../../../utils/format';

// The History card under each section of the verification page: Add / Edit /
// Verification / Acknowledgement history for KYC, Bank and Nominees. The
// backend groups the entries (utils/kyc_history_book.py → flow.history_book).

export type HistorySection = 'kyc' | 'bank' | 'nominees';
type Kind = 'added' | 'edited' | 'verified' | 'ack';

interface Row { field?: string; unit_id?: string | null; value?: any; old?: any; new?: any; item?: string; version?: string | null }
interface Ev {
    kind: Kind; timestamp: string; by: string; by_name: string; role: 'Investor' | 'Staff'; on_behalf: boolean;
    // A staff member's own role(s), e.g. "Employee" or "Super Admin" (falls back to "Staff").
    staff_role?: string | null;
    units: string[]; rows: Row[]; reason: string | null; reverify: boolean; version?: string | null;
    // The client's written request staff attached when changing a verified value.
    proof?: { url: string; source?: string | null } | null;
}

const PROOF_SOURCE: Record<string, string> = { email: 'Email', whatsapp: 'WhatsApp', call: 'Phone call', other: 'Other' };
export interface HistoryBookSection {
    added: Ev[]; edited: Ev[]; verified: Ev[]; ack: Ev[];
    counts: Record<Kind, number>; total: number;
    ack_pending: { sent_at: string; version?: string | null; by_name?: string | null } | null;
}

const SECTIONS: { key: HistorySection; label: string; name: string }[] = [
    { key: 'kyc', label: 'KYC', name: 'KYC' },
    { key: 'bank', label: 'Bank', name: 'Bank' },
    { key: 'nominees', label: 'Nominees', name: 'Nominee' },
];

const KINDS: { key: Kind; label: string; sub: string; empty: string }[] = [
    { key: 'added', label: 'Added', sub: 'Every time fields were added', empty: 'Nothing has been added yet.' },
    { key: 'edited', label: 'Edited', sub: 'Every change, old → new value', empty: 'No edits — the details are as first added.' },
    { key: 'verified', label: 'Verified', sub: 'Every check and the fields checked', empty: 'Nothing has been verified yet.' },
    { key: 'ack', label: 'Acknowledged', sub: 'Every confirmation by the investor', empty: 'Not acknowledged yet.' },
];
// Timeline dot per kind — the only colour in the card.
const DOT: Record<Kind, string> = {
    added: 'bg-[#4F67D9]', edited: 'bg-[#D98A1C]', verified: 'bg-[#2D9A5A]', ack: 'bg-[#1B4332]',
};

const fmtDay = (ts?: string) => {
    const d = ts ? parseServerTime(ts) : null;
    return d && !isNaN(d.getTime()) ? formatDateToDdmmyyyy(d) : '';
};
const fmtTime = (ts?: string) => {
    const d = ts ? parseServerTime(ts) : null;
    return d && !isNaN(d.getTime()) ? d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '';
};
const fmtMobile = (m?: string) => (/^\d{10}$/.test(m || '') ? `+91 ${m!.slice(0, 5)} ${m!.slice(5)}` : m || '');
const shortVersion = (v?: string | null) => (v ? `v·${String(v).slice(0, 6)}` : '—');

export const historyFieldLabel = (r: Row, unitLabel: (id?: string | null) => string) => {
    const f = r.field || '';
    const label = FIELD_LABELS[f] || NOMINEE_FIELD_LABELS[f] || f.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
    return r.unit_id ? `${unitLabel(r.unit_id)} · ${label}` : label;
};
export const historyValue = (r: Row, v: any): string => {
    if (v === null || v === undefined || v === '') return '—';
    if (r.field === 'value_type') return valueTypeLabel(String(v)) || String(v);
    if (r.field === 'mou_type') return mouTypeLabel(String(v)) || String(v);
    if (typeof v === 'string' && /^https?:\/\//.test(v)) return 'File uploaded';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
};
const isLink = (v: any) => typeof v === 'string' && /^https?:\/\//.test(v);
// A field whose value (or old / new value) is an uploaded file.
const isFileRow = (r: Row) => isLink(r.value) || isLink(r.old) || isLink(r.new);

// An uploaded file shows as a small preview that opens in the viewer pop-up (not a new tab).
const Value: React.FC<{ r: Row; v: any; className?: string }> = ({ r, v, className }) => {
    if (!isLink(v)) return <span className={className}>{historyValue(r, v)}</span>;
    const label = FIELD_LABELS[r.field || ''] || NOMINEE_FIELD_LABELS[r.field || ''] || 'Document';
    return (
        <DocViewer url={v} label={label}
            className="block w-28 h-20 rounded-lg overflow-hidden ring-1 ring-slate-200 bg-slate-50 hover:ring-slate-400 transition">
            {/\.pdf($|[?#])/i.test(decodeURIComponent(v))
                ? <span className="w-full h-full grid place-items-center text-[11px] font-semibold text-slate-600">PDF · view</span>
                : <DocImg src={v} alt={label} lazy className="w-full h-full object-cover" />}
        </DocViewer>
    );
};

interface Props {
    book: Record<HistorySection, HistoryBookSection> | undefined;
    // flow.field_audit: each field's edits, which carry the proof staff attached.
    fieldAudit?: Record<string, any>;
    section: HistorySection;
    sectionStatus?: Record<string, string>;
    units?: { unit_id: string; unit_index: number }[];
    investorName?: string;
    mobile?: string;
    // flow.investor.added_by: who added the investor (values null when not recorded).
    addedBy?: { name: string | null; mobile: string | null; role: string | null; country_code?: string | null } | null;
    addedAt?: string | null;
}

const KycHistoryCard: React.FC<Props> = ({ book, fieldAudit, section, units = [], addedBy, addedAt }) => {
    const [kind, setKind] = useState<Kind>('added');
    if (!book) return null;
    // Proof for an edit event: the field_audit edit made by the same person at the same moment.
    const proofs: Record<string, { url: string; source?: string | null }> = {};
    Object.values(fieldAudit || {}).forEach((a: any) => (a?.edits || []).forEach((x: any) => {
        if (x?.proof_url) proofs[`${x.timestamp}|${x.by}`] = { url: x.proof_url, source: x.proof_source };
    }));
    const proofOf = (e: Ev) => e.proof || proofs[`${e.timestamp}|${e.by}`] || null;
    const sec = book[section];
    const meta = KINDS.find(k => k.key === kind)!;
    const events = sec[kind] || [];
    const unitLabel = (id?: string | null) => {
        const u = units.find(x => x.unit_id === id);
        return u ? `Unit ${u.unit_index + 1}` : (id || '');
    };
    const unitsChip = (ids: string[]) => {
        const nums = ids.map(id => units.find(u => u.unit_id === id)?.unit_index).filter((n): n is number => n !== undefined).map(n => n + 1).sort((a, b) => a - b);
        if (!nums.length) return '';
        if (nums.length === 1) return `Unit ${nums[0]}`;
        const run = nums.every((n, i) => !i || n === nums[i - 1] + 1);
        return run ? `Units ${nums[0]}–${nums[nums.length - 1]}` : `Units ${nums.join(', ')}`;
    };

    return (
        <section className="bg-white rounded-2xl ring-1 ring-slate-200" aria-label="History">
            <header className="px-6 pt-5">
                <h2 className="text-[16px] font-bold text-slate-900">{SECTIONS.find(s => s.key === section)?.label} history</h2>
                {/* Who added the investor: the label, then the same who | role | mobile | when line an event has. */}
                <div className="mt-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Investor added by</div>
                <div className="mt-1 flex flex-wrap items-center gap-y-1 text-[13px] text-slate-600 [&>*+*]:ml-3 [&>*+*]:pl-3 [&>*+*]:border-l [&>*+*]:border-slate-300">
                    {addedBy?.mobile ? (<>
                        <span className="font-semibold text-slate-900">{addedBy.name || addedBy.mobile}</span>
                        {addedBy.role && <span>{addedBy.role}</span>}
                        <span className="tabular-nums">{withCountryCode(addedBy.mobile, addedBy.country_code)}</span>
                        {addedAt && <time className="tabular-nums whitespace-nowrap">{fmtDay(addedAt)}, {fmtTime(addedAt)}</time>}
                    </>) : <span className="text-slate-400">Not recorded</span>}
                </div>
            </header>

            <div role="tablist" aria-label="History type" className="flex gap-6 px-6 mt-4 border-b border-slate-200 overflow-x-auto">
                {KINDS.map(k => {
                    const on = kind === k.key;
                    return (
                        <button key={k.key} type="button" role="tab" aria-selected={on} onClick={() => setKind(k.key)}
                            className={`-mb-px h-11 inline-flex items-center gap-2 border-b-2 text-[13px] font-semibold whitespace-nowrap transition-colors ${on ? 'border-[#1B4332] text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
                            {k.label}
                            <span className={`min-w-[20px] h-5 px-1.5 rounded-full inline-grid place-items-center text-[11px] font-semibold tabular-nums ${on ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}>{sec.counts[k.key]}</span>
                        </button>
                    );
                })}
            </div>

            <div className="px-6 py-5">
                {!events.length && !(kind === 'ack' && sec.ack_pending) && (
                    <div className="py-8 text-center text-[13px] text-slate-500 rounded-xl bg-slate-50">{meta.empty}</div>
                )}

                <ol className="relative">
                    {events.map((e, i) => {
                        const last = i === events.length - 1 && !(kind === 'ack' && sec.ack_pending);
                        return (
                            <li key={`${e.timestamp}-${i}`} className="relative pl-6 pb-6 last:pb-0">
                                {!last && <span className="absolute left-[3px] top-3 bottom-0 w-px bg-slate-200" aria-hidden />}
                                <span className={`absolute left-0 top-[7px] w-[7px] h-[7px] rounded-full ${DOT[e.kind]}`} aria-hidden />

                                {/* Who | role | mobile | when | units — a vertical line between each. */}
                                <div className="flex flex-wrap items-center gap-y-1 text-[13px] text-slate-600 [&>*+*]:ml-3 [&>*+*]:pl-3 [&>*+*]:border-l [&>*+*]:border-slate-300">
                                    <span className="font-semibold text-slate-900">{e.by_name}</span>
                                    <span>{e.role === 'Staff' ? `${e.staff_role || 'Staff'}${e.on_behalf ? ', for investor' : ''}` : e.role}</span>
                                    {fmtMobile(e.by) && <span className="tabular-nums">{fmtMobile(e.by)}</span>}
                                    <time className="tabular-nums whitespace-nowrap">{fmtDay(e.timestamp)}, {fmtTime(e.timestamp)}</time>
                                    {e.units.length > 0 && <span>{unitsChip(e.units)}</span>}
                                </div>

                                {/* Each field a block sized to its text — name on top, value below — side by side, wrapping. */}
                                {kind === 'verified' && e.rows.every(r => r.value === null || r.value === undefined || r.value === '') ? (
                                    // Verified from the dashboard without the value sent along: just the fields.
                                    <div className="mt-2.5 rounded-xl ring-1 ring-slate-200 px-4 py-3 text-[13px] text-slate-700 leading-relaxed">
                                        <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Fields verified</span>
                                        {e.rows.map(r => historyFieldLabel(r, unitLabel)).join(', ')}
                                    </div>
                                ) : (
                                    <>
                                        {/* Text fields in one row, uploaded files (images / PDFs) in their own row below. */}
                                        {[e.rows.filter(r => !isFileRow(r)), e.rows.filter(isFileRow)].filter(group => group.length).map((group, g) => (
                                            <dl key={g} className="mt-2.5 flex flex-wrap items-start gap-2">
                                                {group.map((r, j) => (
                                                    <div key={j} className="max-w-full rounded-lg ring-1 ring-slate-200 bg-white px-3.5 py-2.5">
                                                        <dt className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{kind === 'ack' && !r.field ? r.item : historyFieldLabel(r, unitLabel)}</dt>
                                                        <dd className="mt-1 text-[13px] break-words">
                                                            {kind === 'edited' ? (
                                                                // Old → new on one line (thumbnails too).
                                                                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                                                    <span className="text-slate-500 line-through decoration-slate-400" title="From"><Value r={r} v={r.old} /></span>
                                                                    <span className="text-slate-400 font-semibold" aria-label="changed to">→</span>
                                                                    <span className="font-medium text-slate-900" title="To"><Value r={r} v={r.new} /></span>
                                                                </span>
                                                            ) : kind === 'ack' && !r.field
                                                                // Older acknowledgments with no field values recorded: just what and which version.
                                                                ? <span className="font-mono text-[12px] text-slate-700" title={r.version || undefined}>{shortVersion(r.version)}</span>
                                                                : <span className="font-medium text-slate-900"><Value r={r} v={r.value} /></span>}
                                                        </dd>
                                                    </div>
                                                ))}
                                            </dl>
                                        ))}
                                    </>
                                )}

                                {e.reason && <p className="mt-2 text-[13px] text-slate-600"><span className="font-semibold text-slate-800">{kind === 'ack' ? 'Remarks' : 'Reason'}:</span> {e.reason}</p>}
                                {proofOf(e)?.url && (
                                    <div className="mt-2.5">
                                        <p className="text-[12px] text-slate-500 mb-1.5">Proof{proofOf(e)!.source ? ` · ${PROOF_SOURCE[proofOf(e)!.source!] || proofOf(e)!.source}` : ''}</p>
                                        <DocViewer url={proofOf(e)!.url} label="Proof of the client's request"
                                            className="block w-36 h-24 rounded-lg overflow-hidden ring-1 ring-slate-200 bg-slate-50 hover:ring-slate-400 transition">
                                            {/\.pdf($|[?#])/i.test(decodeURIComponent(proofOf(e)!.url))
                                                ? <span className="w-full h-full grid place-items-center text-[11px] font-semibold text-slate-600">PDF · view</span>
                                                : <DocImg src={proofOf(e)!.url} alt="Proof of the client's request" lazy className="w-full h-full object-cover" />}
                                        </DocViewer>
                                    </div>
                                )}
                                {e.reverify && <p className="mt-2 text-[12px] font-medium text-amber-700">Changed after verification — re-verification needed</p>}
                            </li>
                        );
                    })}
                    {kind === 'ack' && sec.ack_pending && (
                        <li className="relative pl-6">
                            <span className="absolute left-0 top-[7px] w-[7px] h-[7px] rounded-full ring-1 ring-slate-400 bg-white" aria-hidden />
                            <p className="text-[13px] text-slate-600"><span className="font-semibold text-slate-900">Acknowledgment pending</span>
                                <span className="mx-1.5 text-slate-300">·</span>link sent {fmtDay(sec.ack_pending.sent_at)}, {fmtTime(sec.ack_pending.sent_at)}{sec.ack_pending.by_name ? ` by ${sec.ack_pending.by_name}` : ''}</p>
                        </li>
                    )}
                </ol>
            </div>
        </section>
    );
};

export default KycHistoryCard;
